import { format, parseISO, differenceInCalendarDays, isAfter, isValid, startOfDay } from 'date-fns';
import { he } from 'date-fns/locale';

import { MIN_BIRTH_YEAR } from 'src/components/pages/Main/HeroForm/BirthDatePicker/constants';
import data from 'src/data/data.json';

const BIRTH_DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export enum Category {
  WAR = 'מלחמה',
  MILITARY_OPERATION = 'מבצע צבאי',
  TERRORIST_OPERATIONS = 'פעולות טרור',
}

export type TimelineCategory = Category | 'peaceful';

export const CATEGORY_PRIORITY: Record<TimelineCategory, number> = {
  peaceful: 0,
  [Category.TERRORIST_OPERATIONS]: 1,
  [Category.MILITARY_OPERATION]: 2,
  [Category.WAR]: 3,
};

export const CATEGORY_COLORS: Record<TimelineCategory, string> = {
  peaceful: '#f9fbfa',
  [Category.TERRORIST_OPERATIONS]: '#feb161',
  [Category.MILITARY_OPERATION]: '#a35139',
  [Category.WAR]: '#9B1B4D', // '#69092e',
};

export interface LifetimeSegment {
  category: Category;
  widthPercent: number;
  lifeTimeStartPercent: number;
}

const CATEGORY_LEGEND: { category: TimelineCategory; label: string }[] = [
  { category: 'peaceful', label: 'שגרה' },
  { category: Category.WAR, label: 'מלחמה' },
  { category: Category.MILITARY_OPERATION, label: 'מבצע צבאי' },
  { category: Category.TERRORIST_OPERATIONS, label: 'גלי טרור' },
];

export interface EventRecord {
  category: Category;
  name: string;
  duration: number;
  startDate: string;
  endDate: string;
}

export interface PersonalStats {
  birthDate: string;
  stats: {
    lifeTimeTotalDays: number;
    warTimeDays: number; // all categories
    percentage: number; // warTimeDays / lifeTimeDays
    wars: number; // amount of Category.WAR records
    militaryOperations: number; // amount of Category.MILITARY_OPERATION records
    terroristOperations: number; // amount of Category.TERRORIST_OPERATIONS records
  };
  timeline: EventRecord[];
}

interface DateInterval {
  start: Date;
  end: Date;
}

/** Inclusive calendar days between start and end. */
const inclusiveCalendarDays = (start: Date, end: Date): number => differenceInCalendarDays(end, start) + 1;

const eventEndDate = (event: EventRecord, today: Date): Date => (event.endDate ? parseISO(event.endDate) : today);

/** True when the event overlaps [birthdate, today]. */
const eventOverlapsLifetime = (event: EventRecord, birthdate: Date, today: Date): boolean => {
  const start = parseISO(event.startDate);
  const end = eventEndDate(event, today);
  return start <= today && end >= birthdate;
};

/** Clip event to [birthdate, today]; null if no overlap. */
const clipEventToLifetime = (event: EventRecord, birthdate: Date, today: Date): DateInterval | null => {
  if (!eventOverlapsLifetime(event, birthdate, today)) return null;

  const start = parseISO(event.startDate);
  const end = eventEndDate(event, today);

  return {
    start: start < birthdate ? birthdate : start,
    end: end > today ? today : end,
  };
};

/** Merge overlapping inclusive intervals; adjacent non-overlapping intervals stay separate. */
const mergeOverlappingIntervals = (intervals: DateInterval[]): DateInterval[] => {
  if (!intervals.length) return [];

  const sorted = [...intervals].sort((a, b) => a.start.getTime() - b.start.getTime());
  const merged: DateInterval[] = [{ start: sorted[0].start, end: sorted[0].end }];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    if (differenceInCalendarDays(current.start, last.end) <= 0) {
      if (differenceInCalendarDays(current.end, last.end) > 0) {
        last.end = current.end;
      }
    } else {
      merged.push({ start: current.start, end: current.end });
    }
  }

  return merged;
};

const sumIntervalDays = (intervals: DateInterval[]): number =>
  intervals.reduce((sum, { start, end }) => sum + inclusiveCalendarDays(start, end), 0);

const countByCategory = (events: EventRecord[], category: Category, birthdate: Date, today: Date): number =>
  events.filter((event) => event.category === category && eventOverlapsLifetime(event, birthdate, today)).length;

/** Contiguous lifetime bar segments with highest-priority category per day. */
export const buildLifetimeSegments = (birthdate: Date, events: EventRecord[], totalDays: number): LifetimeSegment[] => {
  if (totalDays <= 0) return [];

  const lifetimeEvents: {
    category: Category;
    widthPercent: number;
    lifeTimeStartPercent: number;
  }[] = [];

  for (const event of events) {
    const clipped = clipEventToLifetime(event, birthdate, new Date());
    if (!clipped) continue;

    lifetimeEvents.push({
      category: event.category,
      widthPercent: ((differenceInCalendarDays(clipped.end, clipped.start) + 1) / totalDays) * 100,
      lifeTimeStartPercent: (differenceInCalendarDays(clipped.start, birthdate) / totalDays) * 100,
    });
  }

  return lifetimeEvents;
};

export const getLifetimeLegend = () => CATEGORY_LEGEND;

export const formatEventDateRange = (event: EventRecord, today: Date): string => {
  const start = format(parseISO(event.startDate), 'MMMM yyyy', { locale: he });
  const endDate = event.endDate ? parseISO(event.endDate) : today;
  const end = format(endDate, 'MMMM yyyy', { locale: he });
  return `${start} - ${end}`;
};

export const LIFETIME_YEAR_TICK_COUNT = 11;

export const getLifetimeYearAxis = (birthdate: Date, today: Date) => ({
  birthYear: birthdate.getFullYear(),
  endYear: today.getFullYear(),
  tickCount: LIFETIME_YEAR_TICK_COUNT,
});

/** Parse and validate the `d` birth-date query param (same rules as BirthDatePicker). */
export const parseBirthDateParam = (value: string | null | undefined): Date | null => {
  if (!value || !BIRTH_DATE_PARAM_PATTERN.test(value)) return null;

  const date = parseISO(value);
  if (!isValid(date)) return null;

  const today = startOfDay(new Date());
  const birthdate = startOfDay(date);

  if (isAfter(birthdate, today)) return null;
  if (birthdate.getFullYear() < MIN_BIRTH_YEAR) return null;

  return birthdate;
};

/**
 * Calculate war/combat days in the user's lifetime, merging overlapping events.
 */
export const getPersonalStats = (birthdate: Date): PersonalStats => {
  const events = data as EventRecord[];
  const today = new Date();
  const lifeTimeTotalDays = differenceInCalendarDays(today, birthdate) + 1;

  const lifetimeEvents = events
    .filter((event) => eventOverlapsLifetime(event, birthdate, today))
    .sort((a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime());

  const clippedIntervals = lifetimeEvents
    .map((event) => clipEventToLifetime(event, birthdate, today))
    .filter((interval): interval is DateInterval => interval !== null);

  const warTimeDays = sumIntervalDays(mergeOverlappingIntervals(clippedIntervals));
  const percentage = lifeTimeTotalDays > 0 ? Math.floor((warTimeDays / lifeTimeTotalDays) * 100) : 0;

  return {
    birthDate: birthdate.toISOString(),
    stats: {
      lifeTimeTotalDays,
      warTimeDays,
      percentage,
      wars: countByCategory(events, Category.WAR, birthdate, today),
      militaryOperations: countByCategory(events, Category.MILITARY_OPERATION, birthdate, today),
      terroristOperations: countByCategory(events, Category.TERRORIST_OPERATIONS, birthdate, today),
    },
    timeline: lifetimeEvents,
  };
};
